import * as React from "react";
import { isCaseViewModalEnabled } from "app/infrastructure/hosting";
import { routeConstants } from "app/routes/route-constants";

export const isMobileScreen = () => {
  const MOBILE_SCREEN_WIDTH: number = 425;
  return window.innerWidth < MOBILE_SCREEN_WIDTH;
};

export const isWindowLargeEnough = () => {
  const LARGE_WINDOW_WIDTH: number = 1350;
  return window.innerWidth > LARGE_WINDOW_WIDTH;
};

export const dropdownRefContextMenu = (subMenuRef: HTMLUListElement) => {
  if (subMenuRef) {
    if (isMobileScreen()) {
      return;
    }
    if (!isWindowLargeEnough()) {
      subMenuRef.style.left = 'auto';
      subMenuRef.style.right = '100%';
      subMenuRef.style.top = '-10px';
    } else {
      subMenuRef.style.left = '100%';
      subMenuRef.style.right = 'auto';
      subMenuRef.style.top = '-10px';
    }
    const rect = subMenuRef?.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const offset = 50;
    if (rect?.bottom > (viewportHeight - offset)) {
      const overflow = rect.bottom - viewportHeight;
      subMenuRef.style.top = `${parseFloat(subMenuRef.style.top) - overflow}px`;
    }
  }
};

export const isCaseViewModalOpen = () => {
  return !!document.getElementById("caseViewModal");
};

export const getCurrentUrlPath = (): string => {
  return window.location.pathname;
};

export const inStartupDashboard = () => {return !!document.getElementById('startup-dashboard')};

export const isMyCaseDrafts = () => getCurrentUrlPath().includes(routeConstants.myCaseDraftsView);

export const getPathPrefix = () => inStartupDashboard() ? "/startup/dashboard" : "";

export const shouldNavigateToModal = (isCaseReport?: boolean, isDashboard?: boolean, isBowtie?: boolean ) => {
  return isCaseViewModalEnabled() && (isCaseReport || isBowtie || isDashboard || inStartupDashboard() || isMyCaseDrafts());
};

export const shouldOpenCaseInModal = (openInModal: boolean) => {
  return isCaseViewModalEnabled() && !isCaseViewModalOpen() && openInModal;
};

const addOrReplaceQueryParams = (
  searchParams: Record<string, string> | URLSearchParams,
  newParams: Record<string, string>
): string => {
  const updatedParams = new URLSearchParams(
    searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams)
  );

  for (const [key, value] of Object.entries(newParams)) {
    updatedParams.append(key, value);
  }

  const queryString = updatedParams.toString();
  return queryString ? `?${queryString}` : "";
};

export const getUrlOpenCase = (caseId: number, pathName?: string, searchParams?: URLSearchParams): string => {
  if (isCaseViewModalEnabled()) {
    const pathWithoutTrailingSlash = pathName?.endsWith('/') ? pathName?.slice(0, -1) : pathName;
    const casePath = inStartupDashboard() ? '/startup/dashboard/case' : '/case';
    return `${pathWithoutTrailingSlash ? pathWithoutTrailingSlash : ''}${casePath}/${caseId}${addOrReplaceQueryParams(searchParams, {})}`;
  }
  return caseId ? routeConstants.caseViewFor(caseId) : '';
};

export const sanitizeRedirectTarget = (target: string) => {
  try {
    const url = new URL(target, window.location.origin);
    if (
      url.origin === window.location.origin && 
      url.pathname.startsWith("/") &&
      !url.pathname.startsWith("//")
    ) {
      return url.pathname + url.search + url.hash;
    }
  } catch (e) {
    return null;
  }
  return null;
};

export const extractCaseIdFromUrl = (url: string): number | null => {
  try {
    const path = new URL(url, window.location.origin).pathname; 
    // e.g. "/fav/case/362457?expandAll=true"  or "/case/362457" or "/bowtie/362457"
    const match = path.match(/\/case\/(\d+)/) || path.match(/\/bowtie\/(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  } catch {
    return null;
  }
};

export const normalizeUrl = (url) => {
  const [base, ...parts] = url.split("?");
  if (parts.length === 0) {return url}

  // join everything after the first '?' with '&'
  const query = parts.join("&");

  return base + "?" + query.replace(/&&+/g, "&");
};

export const getItemsBeforeKey = (arr: any[] = [], key: string): any[] => {
  if (!Array.isArray(arr)) {return []}
  const index = arr.findIndex(obj => obj.hasOwnProperty(key));
  if (index === -1) {return []}
  return arr?.slice(0, index);
};

export const getItemsAfterKey = (arr: any[] = [], key: string): any[] => {
  if (!Array.isArray(arr)) {return []}
  const index = arr.findIndex(obj => obj.hasOwnProperty(key));
  if (index === -1) {return []}
  return arr.slice(index + 1);
};

export const buildDraftUrl = (
  draftNumber: number,
  favouriteId?: number,
  dashboardType?: number,
  bowtieCaseId?: number
) => {
  if (shouldNavigateToModal(!!favouriteId, !!dashboardType, !!bowtieCaseId)) {
     if (bowtieCaseId) {
      return routeConstants.bowtieViewEditCaseFor(bowtieCaseId, draftNumber);
    }
    if (dashboardType) {
      return routeConstants.dashboardWithFavouriteEditCaseFor(dashboardType, favouriteId, draftNumber);
    }
    if (inStartupDashboard()) {
      return routeConstants.startupDashboardEditCaseFor(draftNumber);
    }
    if (isMyCaseDrafts()) {
      return routeConstants.myCaseDraftsViewEditCaseFor(draftNumber);
    }
    return routeConstants.favouriteViewEditCaseFor(favouriteId, draftNumber);
  }
  
  return routeConstants.draftViewFor(draftNumber);
};

export const removeCaseDraftSearchParams = (searchParams: URLSearchParams) => {
  searchParams.delete("section");
  searchParams.delete("status");
  searchParams.delete("addLossPotential");
  searchParams.delete("addResidual");
};

export const isModifiedClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
  return e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey;
};
